import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import {
  CubeIcon,
  TicketIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'

interface DashboardStats {
  subscriptions: {
    total: number
    active: number
    expiringSoon: number
  }
  tickets: {
    total: number
    open: number
    urgent: number
    resolved: number
  }
}

const Dashboard: React.FC = () => {
  const { user } = useAuth()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboardStats()
  }, [])

  const fetchDashboardStats = async () => {
    try {
      // Mock data for now - will be replaced with real API calls
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simulate API delay
      
      setStats({
        subscriptions: {
          total: 5,
          active: 4,
          expiringSoon: 1
        },
        tickets: {
          total: 8,
          open: 3,
          urgent: 1,
          resolved: 5
        }
      })
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error)
    } finally {
      setLoading(false)
    }
  }

  const statCards = [
    {
      title: 'Active Subscriptions',
      value: stats?.subscriptions.active || 0,
      total: stats?.subscriptions.total || 0,
      icon: CubeIcon,
      color: 'bg-green-500',
      href: '/subscriptions'
    },
    {
      title: 'Open Tickets',
      value: stats?.tickets.open || 0,
      total: stats?.tickets.total || 0,
      icon: TicketIcon,
      color: 'bg-vcns-accent',
      href: '/tickets'
    },
    {
      title: 'Urgent Issues',
      value: stats?.tickets.urgent || 0,
      total: stats?.tickets.total || 0,
      icon: ExclamationTriangleIcon,
      color: 'bg-red-500',
      href: '/tickets?priority=urgent'
    },
    {
      title: 'Expiring Soon',
      value: stats?.subscriptions.expiringSoon || 0,
      total: stats?.subscriptions.total || 0,
      icon: ClockIcon,
      color: 'bg-yellow-500',
      href: '/subscriptions?filter=expiring'
    }
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vcns-accent"></div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Dashboard - VCNS Support Portal</title>
      </Helmet>

      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-8">
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome back, {user?.fullName}
            </h1>
            <p className="mt-2 text-gray-600">
              Here's an overview of your subscriptions and support requests.
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {statCards.map((card, index) => (
            <Link
              key={index}
              to={card.href}
              className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`w-8 h-8 ${card.color} rounded-md flex items-center justify-center`}>
                      <card.icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {card.title}
                      </dt>
                      <dd className="flex items-baseline">
                        <div className="text-2xl font-semibold text-gray-900">
                          {card.value}
                        </div>
                        {card.total > 0 && (
                          <div className="ml-2 text-sm text-gray-500">
                            of {card.total}
                          </div>
                        )}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Recent Activity */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Recent Activity</h2>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">
                      Ticket #002 resolved - Billing cycle question
                    </p>
                    <p className="text-xs text-gray-500">2 hours ago</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <ClockIcon className="w-5 h-5 text-yellow-500 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">
                      Microsoft 365 E3 renewal due in 30 days
                    </p>
                    <p className="text-xs text-gray-500">1 day ago</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CubeIcon className="w-5 h-5 text-vcns-accent mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">
                      Added 5 Microsoft 365 Business Premium licenses
                    </p>
                    <p className="text-xs text-gray-500">3 days ago</p>
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <Link
                  to="/tickets"
                  className="text-sm font-medium text-vcns-accent hover:text-vcns-secondary"
                >
                  View all activity →
                </Link>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Quick Actions</h2>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-4">
                <Link
                  to="/tickets/new"
                  className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <TicketIcon className="w-6 h-6 text-vcns-accent" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Create Support Ticket</p>
                    <p className="text-xs text-gray-500">Get help with technical issues</p>
                  </div>
                </Link>
                
                <Link
                  to="/subscriptions"
                  className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <CubeIcon className="w-6 h-6 text-green-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Manage Subscriptions</p>
                    <p className="text-xs text-gray-500">View and modify your services</p>
                  </div>
                </Link>

                <a
                  href="https://vcns.tech/mpn/csp/catalog"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                  </svg>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Browse Catalog</p>
                    <p className="text-xs text-gray-500">Add new products and services</p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Dashboard
